// Load matching tab (ST-00218 P2): labels and number formatting shared by the tab, the route
// drawer and the Route Summary preview. Wording is for weighbridge / operations staff: plain,
// specific, no engine jargon. The flag labels are the fixed table of API.md §4.1.
import type {
  ChangeReasonCode,
  DataStatus,
  DispatchRouteStatus,
  DqSummary,
  EngineFigures,
  LegacyDispatchClass,
  RegDiffClass,
  RouteSignoffState,
  RouteWindow,
  Tier,
  TicketFlag,
} from '../../../../types/loadMatching';

const num = (v: number, digits: number): string =>
  v.toLocaleString(undefined, { minimumFractionDigits: digits, maximumFractionDigits: digits });

/** Tonnes, one decimal: `1,750,835.0 t`. */
export const fmtT = (t: number | null | undefined): string =>
  t === null || t === undefined || !Number.isFinite(t) ? '—' : `${num(t, 1)} t`;

/** Signed tonnes: `+110.9 t`, `−117.9 t`. */
export const fmtSignedT = (t: number): string =>
  `${t > 0 ? '+' : t < 0 ? '−' : ''}${num(Math.abs(t), 1)} t`;

/** Loads: `1,234`. */
export const fmtN = (n: number | null | undefined): string =>
  n === null || n === undefined ? '—' : n.toLocaleString();

/** Signed loads: `+26`, `−37`, `0`. */
export const fmtSignedN = (n: number): string =>
  `${n > 0 ? '+' : n < 0 ? '−' : ''}${Math.abs(n).toLocaleString()}`;

/** Sign of a value shown with 2 decimals: none when it rounds to 0.00. */
const sign2 = (p: number): string => (Math.abs(p) < 0.005 ? '' : p > 0 ? '+' : '−');

/** Variance: `−0.11%`, `+0.71%`, `0.00%`; null → `n/a`. */
export const fmtPct = (p: number | null | undefined): string =>
  p === null || p === undefined || !Number.isFinite(p)
    ? 'n/a'
    : `${sign2(p)}${num(Math.abs(p), 2)}%`;

/** Percentage points: `−10.60 pts`, `0.00 pts`; null → `—`. */
export const fmtPts = (p: number | null | undefined): string =>
  p === null || p === undefined || !Number.isFinite(p)
    ? '—'
    : `${sign2(p)}${num(Math.abs(p), 2)} pts`;

/** Minutes as hours: `16 h`, `1 h 30 min`, `45 min`. */
export const fmtMinutes = (m: number | null | undefined): string => {
  if (m === null || m === undefined || !Number.isFinite(m)) return '—';
  const total = Math.round(m);
  const h = Math.floor(total / 60);
  const min = total % 60;
  if (h === 0) return `${min} min`;
  return min ? `${h} h ${min} min` : `${h} h`;
};

/** A transit time: `13 h 48 min`, `2 d 3 h` past a day. */
export const fmtTransit = (m: number | null | undefined): string => {
  if (m === null || m === undefined || !Number.isFinite(m)) return '—';
  if (Math.abs(m) < 24 * 60) return (m < 0 ? '−' : '') + fmtMinutes(Math.abs(m));
  const abs = Math.round(Math.abs(m));
  const d = Math.floor(abs / 1440);
  const h = Math.floor((abs % 1440) / 60);
  return `${m < 0 ? '−' : ''}${d} d${h ? ` ${h} h` : ''}`;
};

/** `2026-08-01` → `1 Aug 2026` (calendar date, no timezone shift). */
export const fmtDay = (ymd: string): string => {
  const [y, mo, d] = ymd.split('-').map(Number);
  if (!y || !mo || !d) return ymd;
  const month = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
  ][mo - 1];
  return `${d} ${month} ${y}`;
};

export const fmtPeriod = (p: { startDate: string; endDate: string }): string =>
  p.startDate === p.endDate ? fmtDay(p.startDate) : `${fmtDay(p.startDate)} – ${fmtDay(p.endDate)}`;

/** The last full calendar month (the API's default period), in the viewer's calendar. */
export function lastFullMonth(now = new Date()): { startDate: string; endDate: string } {
  const first = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  const last = new Date(now.getFullYear(), now.getMonth(), 0);
  const ymd = (d: Date) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  return { startDate: ymd(first), endDate: ymd(last) };
}

// ───────────────────────────────────────────────────────────────── labels ──

export const FLAG_LABELS: Record<TicketFlag, string> = {
  reg_probable_typo: 'probable registration typo (confirmed by delivery no)',
  reg_probable_typo_unconfirmed: 'unconfirmed — probable registration typo',
  reg_mismatch: 'registration keyed incorrectly',
  reg_blank: 'registration blank',
  delivery_no_blank: 'delivery no blank',
  delivery_no_not_found: 'delivery no not found',
  delivery_no_points_elsewhere: 'delivery no points to another load',
  delivery_no_numeric: 'delivery no matched on its number only',
  late: 'late (transit > W)',
  receipt_before_dispatch: 'receipt before dispatch',
  other_route: 'matched across routes',
  date_only_receipt: 'receipt has a date only',
  weight_gap: 'weight differs > 20 %',
  possible_reweigh:
    'possible re-weigh (weighed out again at the same site within 2 h at a similar weight)',
  double_dispatch: 'dispatched again before a receipt',
  probable_duplicate_weighing: 'probable duplicate weighing',
  dispatched_again_before_receipt: 'dispatched again before receipt',
};

/** Short tier names (legend, table) and what each one means (drawer). */
export const TIER_LABELS: Record<Tier, { short: string; long: string }> = {
  1: { short: 'Delivery no + reg', long: 'Delivery no and registration agree' },
  2: { short: 'Truck + time', long: 'Same truck, next receipt after the dispatch' },
  3: { short: 'Delivery no only', long: 'Delivery no agrees, registration does not' },
  4: { short: 'Unconfirmed', long: 'Unconfirmed — probable registration typo' },
};

export const TIERS: readonly Tier[] = [1, 2, 3, 4];

/** UI label of how two registrations differ (API.md §4.2). */
export const regDiffLabel = (c: RegDiffClass): string => {
  switch (c) {
    case 'same':
      return 'same registration';
    case 'blank':
      return 'registration blank';
    case 'confusable1':
    case 'confusableN':
      return 'probable typo (O/0)';
    case 'subst1':
    case 'transpose':
    case 'indel1':
    case 'suffix':
      return 'one keystroke';
    default:
      return 'different truck?';
  }
};

export const OLD_CLASS_LABELS: Record<LegacyDispatchClass, string> = {
  matched: 'Matched',
  carried_forward: 'Carried forward',
  brought_forward: 'Brought forward',
  counted_no_matching: 'Counted (no matching)',
  not_counted: 'Not counted',
};

export const NEW_STATUS_LABELS: Record<DispatchRouteStatus, string> = {
  matched: 'Matched',
  brought_forward: 'Brought forward',
  carried_forward_received_after: 'Carried forward (received after the period)',
  carried_forward_in_transit: 'Carried forward (in transit)',
  carried_forward_received_before: 'Carried forward (received before the period)',
  carried_forward_pending: 'Carried forward (not matched yet)',
  missing: 'Missing',
  needs_review: 'Needs review',
  received_off_route: 'Received off-route',
  no_receipt_data: 'No receipt data',
  not_counted: 'Not counted',
};

export const REASON_LABELS: Record<ChangeReasonCode, string> = {
  now_matched: 'Now matched',
  now_missing: 'Now missing',
  now_needs_review: 'Now needs review',
  now_carried_forward: 'Now carried forward',
  now_brought_forward: 'Now brought forward',
  no_longer_brought_forward: 'No longer brought forward',
  received_off_route: 'Received off-route',
  no_receipt_data: 'No receipt data',
  pending: 'Not matched yet',
};

export const SIGNOFF_STATE_LABELS: Record<RouteSignoffState['state'], string> = {
  none: 'Not signed off',
  signed: 'Signed off',
  switched: 'Uses Truck + time',
  reverted: 'Delivery number',
};

export const MATCH_MODE_LABELS: Record<'exact' | 'numeric' | 'none' | 'mixed', string> = {
  exact: 'exact',
  numeric: 'number only',
  none: 'none',
  mixed: 'mixed',
};

export const WINDOW_SOURCE_LABELS: Record<RouteWindow['source'], string> = {
  override: 'set by hand',
  frozen: 'frozen at switch-over',
  learned: 'learned from this route',
  default: 'default (too few matched loads to learn)',
};

/** Why a route's new BF / CF / variance read n/a (when it has no config hint saying so). */
export const DATA_STATUS_TEXT: Record<DataStatus, string> = {
  ok: '',
  no_receipt_data: 'n/a — no receipt data',
  partial_receipt_data: 'n/a — receipt data incomplete',
  no_dispatch_data: 'n/a — no dispatch data',
  empty: 'No loads in this period',
};

/** True when the new figures are not comparable (BF, CF, missing, variance show n/a). */
export const isNa = (f: Pick<EngineFigures, 'dataStatus'>): boolean => f.dataStatus !== 'ok';

/** Number of data-quality findings worth a look (the drawer tab badge). */
export const dqIssueCount = (s: DqSummary): number =>
  s.regProbableTypo +
  s.regMismatch +
  s.regUnconfirmed +
  s.deliveryNoBlank +
  s.deliveryNoNotFound +
  s.deliveryNoPointsElsewhere +
  s.missing +
  (s.needsReview ?? 0) +
  (s.implausibleTimestamps ?? 0) +
  s.orphans +
  s.receivedOffRoute +
  s.late +
  // 1.1.0: possible re-weighs are the probable duplicate weighings, already in needsReview
  (typeof s.needsReview === 'number' ? 0 : s.possibleReweigh);

export const DQ_INDICATOR_LABELS: Record<DqSummary['indicator'], string> = {
  ok: 'OK',
  review: 'Review',
  action: 'Action needed',
};
